import { JSXElementConstructor } from 'react';
import * as React from 'react';
import { Control, FieldValues } from 'react-hook-form';

import { FieldNameFromFormShape } from '@/types/react-hook-form';

export interface FormAwareInputProps<FormShape extends FieldValues> {
  fieldName: FieldNameFromFormShape<FormShape>;
  control: Control<FormShape>;
  label: string;
  hideLabel?: boolean;
  /**
   * If given, the suggestion locator icon will be shown at the end of the label.
   * This is the default behavior for FormAwareInput.
   */
  labelIconEnd?: React.ReactNode;
}

export type FormAwareElementProps<
  S extends FieldValues,
  T extends FormAwareInputProps<S>,
> = JSXElementConstructor<T>;

export enum SuggestionLocatorPosition {
  /**
   * Default position for the locator icon. This will show the icon in the label
   * of the input field.
   */
  TOP = 'top',
  /**
   * This will show the icon to the right of the input field.
   */
  RIGHT = 'right',
}

/**
 * Additional props passed to the "render" function of a FormAwareInputRepeater.
 */
export interface AdditionalItemRendererProps {
  // This is called by the useEffect of an item on render to determine if the
  // item has a locator button.
  onRenderHasLocatorButton?: (value: boolean) => void;
  // If true, the labels of all fields will be hidden. This is used when the
  // first "existing" item is duplicated by a suggestion. The label will be
  // displayed on the "suggested" item.
  hideLabelOverride?: boolean;
  // If true, the suggestion icon will be hidden on the "main" field of the
  // input item. This is used when the "existing" item is duplicated by a
  // suggestion. The suggestion icon will be displayed on the "suggested" item.
  hideLocateSuggestionIconOverride?: boolean;
  // If true, we'll show the AI sparkle icon in the "main" field input. This is
  // used to indicate that the input item is a suggestion, or existing field
  // within a duplicate group.
  showAISparkleIcon?: boolean;
  // If true, the delete button will be hidden.
  hideDeleteButton?: boolean;
  /**
   * Setting this indicates we're rendering the form input in the "duplicate
   * resolution" card, allowing the user to select which item to keep.
   */
  duplicateResolutionConfigs?: FormAwareInputItemDuplicateResolutionProps;
  locatorPosition?: SuggestionLocatorPosition;
}

export interface FormAwareInputItemDuplicateResolutionProps {
  isSelected: boolean;
  /**
   * The callback when the radio button for the input item row is clicked.
   */
  onSelect: () => void;
  /**
   * The callback when the confirm button for the input item row is clicked.
   * This is used to confirm that this item will be saved to the form, and the
   * other one in the duplicate group will be removed.
   */
  onConfirm: () => void;
}
